import { forwardRef, useRef } from 'react';
import type { PropsWithoutRef } from 'react';
import { render, unmountComponentAtNode } from 'react-dom';

import {
  Tooltip,
  useConsolidatedRef,
  useElement,
  useI18n,
  useConfiguration,
  formatListToLocaleString,
  Icon
} from '@pega/cosmos-react-core';
import type { ForwardRefForwardPropsComponent } from '@pega/cosmos-react-core';

import { StyledEmailEntity } from './Email.styles';
import type { EmailEntityProps } from './Email.types';

const EmailEntity: ForwardRefForwardPropsComponent<EmailEntityProps> = forwardRef(
  function EmailEntity(
    { entity, ...restProps }: PropsWithoutRef<EmailEntityProps>,
    ref: EmailEntityProps['ref']
  ) {
    const [el, setEl] = useElement<HTMLElement>();
    const elementRef = useConsolidatedRef<HTMLElement>(setEl, ref);
    const t = useI18n();
    const { locale } = useConfiguration();
    const tooltipRef = useRef<HTMLDivElement>(null);
    return (
      <StyledEmailEntity
        {...restProps}
        entity={entity}
        ref={elementRef}
        aria-label={
          Array.isArray(entity.description)
            ? formatListToLocaleString(entity.description, t, locale)
            : entity.description
        }
        onContextMenu={() => {
          if (tooltipRef.current) tooltipRef.current.hidden = true;
        }}
        contentEditable={false}
      >
        {entity.value}
        {entity.icon && <Icon name={entity.icon} />}
        {entity.description && entity.description.length > 0 && (
          <Tooltip target={el} ref={tooltipRef} hideDelay='short'>
            {Array.isArray(entity.description)
              ? formatListToLocaleString(entity.description, t, locale, {
                  separator: '\n'
                })
              : entity.description}
          </Tooltip>
        )}
      </StyledEmailEntity>
    );
  }
);

export class EmailEntityWeb extends HTMLElement {
  removeElements() {
    while (this.firstChild) {
      this.removeChild(this.firstChild);
    }
  }

  connectedCallback() {
    const inner = this.innerHTML;
    this.removeElements();
    const variant = Number(this.getAttribute('data-variant'));
    const descAttribute = this.getAttribute('data-description');
    const description = descAttribute !== null ? JSON.parse(descAttribute) : undefined;
    const type = this.getAttribute('data-type') || '';
    render(
      <EmailEntity
        entity={{
          value: inner,
          type,
          variant,
          description
        }}
      />,
      this
    );
  }

  disconnectedCallback() {
    unmountComponentAtNode(this);
  }
}

if (!customElements.get('pega-email-entity')) {
  customElements.define('pega-email-entity', EmailEntityWeb);
}

export default EmailEntity;
